import React, { useMemo, useState } from "react";
import { Box, Card, Typography, ButtonGroup, Button } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// Compact "X by month" trend card: current year vs. last year, reusing the
// {labels, datasets} shape useDashboardData.js already builds via
// buildYearChart — just a smaller, mockup-styled presentation of the same
// data already powering the fuller "by Year & Month" chart further down.
const TrendCard = ({ title, data, color = "#2f63ad" }) => {
  const [viewMode, setViewMode] = useState("compare");

  const currentYear = new Date().getFullYear();
  const priorYear = currentYear - 1;

  const currentDataset = data?.datasets?.find((d) => String(d.label) === String(currentYear));
  const priorDataset = data?.datasets?.find((d) => String(d.label) === String(priorYear));

  const currentTotal = currentDataset ? currentDataset.data.reduce((a, b) => a + b, 0) : 0;
  const priorTotal = priorDataset ? priorDataset.data.reduce((a, b) => a + b, 0) : 0;

  const chartData = useMemo(() => {
    const datasets = [];
    if ((viewMode === "compare" || viewMode === "prior") && priorDataset) {
      datasets.push({ ...priorDataset, borderColor: "#c7d2e0", backgroundColor: "#c7d2e022", pointRadius: 0 });
    }
    if ((viewMode === "compare" || viewMode === "current") && currentDataset) {
      datasets.push({ ...currentDataset, borderColor: color, backgroundColor: `${color}22`, pointRadius: 0 });
    }
    return { labels: data?.labels || [], datasets };
  }, [viewMode, currentDataset, priorDataset, data?.labels, color]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { grid: { color: "rgba(0,0,0,0.05)" }, ticks: { font: { size: 10 } } },
    },
  };

  const toggleBtnSx = (active) => ({
    textTransform: "none", fontSize: "0.72rem", fontWeight: 600, px: 1.25,
    backgroundColor: active ? color : "transparent", color: active ? "#fff" : "#6b778c",
    borderColor: "rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: active ? color : "rgba(0,0,0,0.04)" },
  });

  return (
    <Card sx={{ borderRadius: "16px", p: 2.5, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 4px 16px rgba(0,0,0,0.04)", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>{title}</Typography>
        <ButtonGroup size="small">
          <Button onClick={() => setViewMode("prior")} sx={toggleBtnSx(viewMode === "prior")}>{priorYear}</Button>
          <Button onClick={() => setViewMode("current")} sx={toggleBtnSx(viewMode === "current")}>{currentYear}</Button>
          <Button onClick={() => setViewMode("compare")} sx={toggleBtnSx(viewMode === "compare")}>Compare</Button>
        </ButtonGroup>
      </Box>

      <Box sx={{ height: 180 }}>
        <Line data={chartData} options={options} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#c7d2e0" }} />
          <Typography variant="caption" sx={{ color: "#6b778c" }}>{priorYear} · {priorTotal}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
          <Typography variant="caption" sx={{ color: "#6b778c" }}>{currentYear} · {currentTotal}</Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default TrendCard;
